import { useContext } from 'react';
import { EditorContext } from '~/contexts/EditorContext';
import {
    MultiPageImageModel,
    MultiPageVideoModel,
    UndoRedoManager,
    ValidationManager,
} from '@bynder-studio/render-core';
import {
    creativeModelFactory,
    ImageManipulationRenderer,
    PlaybackManipulationRenderer,
} from '@bynder-studio/render-web';
import { CreativeAccessType, CreativeType } from 'packages/types/creative';
import { Reviewer } from 'packages/hooks/useVirtualProperties';

type NotProvided = '__NOT_PROVIDED__';

// @ts-ignore
type SmartReturn<T = NotProvided, U> = T extends NotProvided ? U : T;

type EditorData<T, U> = {
    loading: boolean;
    creativeModelFactory: ReturnType<typeof creativeModelFactory>;
    creativeModel: SmartReturn<T, MultiPageImageModel | MultiPageVideoModel>;
    width: number;
    height: number;
    format: string | null;
    initManipulationRenderer: (canvasWrapper, currentFrame) => void;
    manipulationRenderer: SmartReturn<U, PlaybackManipulationRenderer | ImageManipulationRenderer>;
    validationManager: ValidationManager;
    undoRedoManager: UndoRedoManager;
    type: CreativeAccessType;
    creativeType: CreativeType;
    exportIntegrations: any;
    toggleExportIntegration: (exportName) => void;
    changeExportValue: (newValue) => void;
    approvalEnabled: boolean;
    setApprovalEnabled: (enabled: boolean) => void;
    reviewers: Reviewer[];
    addReviewer: (reviewer: Reviewer) => void;
    removeReviewer: (reviewer: Reviewer) => void;
    canUndo: boolean;
    globalsTab: 'Page' | 'Design';
    setGlobalsTab: (tab: 'Page' | 'Design') => void;
    isPublishing: boolean;
    setIsPublishing: (isPublishing: boolean) => void;
    guideManager: undefined;
    assetLoader: undefined;
    frameRate: number;
    modalActive: boolean;
    setModalActive: (active: boolean) => void;
};

export default function useEditor<T = NotProvided, U = NotProvided>() {
    return useContext(EditorContext) as EditorData<T, U>;
}
